import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

// Bulk cold-outreach (owner feature, 2026-10-01): admin selects rows in
// ResultsTable, writes ONE template here with {{firma_adi}}/{{rapor_linki}}
// placeholders, and this dialog (1) prepares a free-report link for every
// selected business that has an email (skipping the rest — never worth the
// paid enrichment cost for a business we can't reach), showing the total
// DataForSEO bill, then (2) sends the filled-in template at a fixed pace so
// the batch doesn't land in the same second. Server-side send is new for
// cold outreach — see backend/outreach_campaign.py and the UWG §7 note on
// OutreachSendRequest in server.py; the confirm checkbox below is the same
// "I accept the risk" acknowledgment the single-send flow already requires
// via MessageDialog's sendGate, just at batch scope.
const REASON_LABELS = {
  no_email: "E-posta yok",
  not_found: "İşletme bulunamadı",
  report_failed: "Rapor oluşturulamadı",
};

// Same two placeholders as backend/outreach_campaign.py's render_template —
// used here only for the admin-facing live preview, the real send always
// renders server-side with the business's real report link.
export const fillOutreachTemplate = (tpl, name, link) =>
  (tpl || "").replace(/\{\{firma_adi\}\}/g, name || "").replace(/\{\{rapor_linki\}\}/g, link || "");

export function BulkOutreachDialog({ open, onOpenChange, businesses, onPrepare, onSend }) {
  const placeIds = useMemo(() => businesses.map((b) => b.place_id), [businesses]);
  const [stage, setStage] = useState("prepare"); // prepare -> review -> sent
  const [preparing, setPreparing] = useState(false);
  const [prepared, setPrepared] = useState(null);
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [paceSeconds, setPaceSeconds] = useState(10);
  const [confirmed, setConfirmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStage("prepare");
    setPrepared(null);
    setSubject("");
    setBodyText("");
    setPaceSeconds(10);
    setConfirmed(false);
    setResult(null);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePrepare = async () => {
    setPreparing(true);
    try {
      const data = await onPrepare(placeIds);
      setPrepared(data);
      setStage("review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hazırlık başarısız oldu.");
    } finally {
      setPreparing(false);
    }
  };

  const readyRows = (prepared?.rows || []).filter((r) => r.ready);
  const notReadyRows = (prepared?.rows || []).filter((r) => !r.ready);
  const previewRow = readyRows[0];

  const handleSend = async () => {
    setSending(true);
    try {
      const data = await onSend({
        placeIds: readyRows.map((r) => r.place_id),
        subjectTemplate: subject,
        bodyTemplate: bodyText,
        paceSeconds: Number(paceSeconds) || 10,
        confirmBulkOutreach: confirmed,
      });
      setResult(data);
      setStage("sent");
      toast.success(`${data.queued} e-posta kuyruğa alındı.`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || "Gönderim başarısız oldu.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-outreach-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Toplu E-posta Gönderimi
          </DialogTitle>
          <DialogDescription>{businesses.length} firma seçili.</DialogDescription>
        </DialogHeader>

        {stage === "prepare" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Önce her firma için ön rapor linki hazırlanır. E-postası olmayan firmalar otomatik atlanır —
              onlar için ücret harcanmaz.
            </p>
            <Button onClick={handlePrepare} disabled={preparing} data-testid="outreach-prepare-button">
              {preparing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Raporları Hazırla
            </Button>
          </div>
        )}

        {stage === "review" && prepared && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-secondary/30 p-3 text-sm">
              <p>
                {prepared.ready_count} / {prepared.total_count} firma gönderime hazır.
              </p>
              {prepared.estimated_cost_usd > 0 && (
                <p className="text-muted-foreground">Tahmini DataForSEO maliyeti: ${prepared.estimated_cost_usd}</p>
              )}
              {notReadyRows.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-muted-foreground">
                  {notReadyRows.map((r) => (
                    <li key={r.place_id}>
                      {r.name || r.place_id}: {REASON_LABELS[r.reason] || r.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="outreach-subject">{"Konu (ör: Merhaba {{firma_adi}})"}</Label>
              <Input
                id="outreach-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="outreach-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="outreach-body">{"Mesaj — {{firma_adi}} ve {{rapor_linki}} istediğiniz yere yazabilirsiniz"}</Label>
              <Textarea
                id="outreach-body"
                rows={8}
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                data-testid="outreach-body-input"
              />
              <p className="text-xs text-muted-foreground">
                Her e-postanın sonuna otomatik olarak bir "artık mesaj istemiyorum" cümlesi eklenir.
              </p>
            </div>

            {previewRow && (subject || bodyText) && (
              <div className="rounded-lg border bg-card p-3 text-sm" data-testid="outreach-preview">
                <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">Önizleme ({previewRow.name})</p>
                <p className="font-medium">{fillOutreachTemplate(subject, previewRow.name, "https://positivup.com/rapor/...")}</p>
                <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                  {fillOutreachTemplate(bodyText, previewRow.name, "https://positivup.com/rapor/...")}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="outreach-pace">Gönderim hızı (saniye/e-posta)</Label>
              <Input
                id="outreach-pace"
                type="number"
                min={3}
                max={300}
                value={paceSeconds}
                onChange={(e) => setPaceSeconds(e.target.value)}
                className="w-32"
                data-testid="outreach-pace-input"
              />
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <Checkbox
                id="outreach-confirm"
                checked={confirmed}
                onCheckedChange={setConfirmed}
                data-testid="outreach-confirm-checkbox"
              />
              <Label htmlFor="outreach-confirm" className="cursor-pointer font-normal">
                Bu firmalara önceden onay almadan toplu e-posta gönderdiğimi biliyorum (UWG §7 riski bana
                aittir) ve sorumluluğu alıyorum.
              </Label>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || !confirmed || readyRows.length === 0 || !subject.trim() || !bodyText.trim()}
              data-testid="outreach-send-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {readyRows.length} Firmaya Gönder
            </Button>
          </div>
        )}

        {stage === "sent" && result && (
          <div className="space-y-2">
            <p className="text-sm" data-testid="outreach-result">
              {result.queued} e-posta kuyruğa alındı, {result.skipped} atlandı. Gönderim arka planda, seçtiğiniz
              hızda devam ediyor.
            </p>
            <p className="text-xs text-muted-foreground">
              Her firmanın gönderim durumunu birkaç dakika sonra arama listesini yenileyerek görebilirsiniz.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Kapat
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
